// CORE-FREEZE (core plan item 7, docs/direction/2026-11-03-core-delivery-plan.md §1): freeze one
// DE or the whole company, and the incident each freeze opens. Behind the company switch
// `settings.flags.core_freeze` (off by default: these routes answer 409 `conflict` while it is off).
//
//   POST freeze                              managers: freeze the company (every agent)
//   POST digital-employees/{id}/freeze       managers: freeze one DE
//   GET  incidents                           managers: the company's incidents, newest first
//   POST incidents/{id}/resolve              managers: lift the freeze (resolve its incident)
//
// While a freeze holds: the agents' writes are refused with the reason (agent policy guardrails),
// their queued runs are held (`held: frozen …`) and new ones are held at dispatch, and queued
// outward sends are held (`settings.freeze.outbox` = `hold`, the default) or cancelled (`cancel`).
// The incident records the blast radius: the runs held, the outbox rows held or cancelled, and
// the agents' writes still undoable. Resolving it releases the held runs and sends.
import { z } from "zod";
import { FreezeOutboxMode, IncidentKind, IncidentScope, IncidentStatus } from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

export const IncidentBlastRadius = z
  .object({
    runs: z.array(Uuid).describe("Queued runs the freeze held"),
    outbox: z.array(Uuid).describe("Outbox rows it held (or cancelled, with outbox_mode cancel)"),
    writes: z.array(Uuid).describe("Agent writes still undoable when it opened (undo records)"),
  })
  .meta({ id: "IncidentBlastRadius" });

export const IncidentView = z
  .object({
    id: Uuid,
    kind: IncidentKind,
    scope: IncidentScope,
    /** The DE of a `digital_employee` incident; null for the company. */
    membership_id: Uuid.nullable(),
    /** The DE's name (null for the company). */
    name: z.string().nullable(),
    cause: z.string(),
    status: IncidentStatus,
    blast_radius: IncidentBlastRadius,
    outbox_mode: FreezeOutboxMode.nullable(),
    opened_by_membership_id: Uuid.nullable(),
    opened_at: Timestamp,
    resolved_by_membership_id: Uuid.nullable(),
    resolved_at: Timestamp.nullable(),
    resolution: z.string().nullable(),
  })
  .meta({ id: "IncidentView" });
export type IncidentView = z.infer<typeof IncidentView>;

export const IncidentList = z.object({ data: z.array(IncidentView) }).meta({ id: "IncidentList" });

export const IncidentListQuery = z.object({
  status: IncidentStatus.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

export const FreezeBody = z
  .strictObject({ cause: z.string().trim().min(1).max(500) })
  .meta({ id: "FreezeBody" });

export const IncidentResolveBody = z
  .strictObject({ resolution: z.string().trim().min(1).max(500).optional() })
  .meta({ id: "IncidentResolveBody" });

export const incidentRoutes = {
  "freeze.company": defineRoute({
    ...core,
    id: "freeze.company",
    method: "post",
    path: `${base}/freeze`,
    tag: "incidents",
    summary:
      "Freeze the company: every agent's writes refused, queued runs held, queued sends held or cancelled; opens an incident",
    access: "manager",
    params: CompanyParams,
    body: FreezeBody,
    response: { status: 201, schema: IncidentView },
    errors: ["conflict"],
    emits: ["incident.opened"],
  }),
  "freeze.digitalEmployee": defineRoute({
    ...core,
    id: "freeze.digitalEmployee",
    method: "post",
    path: `${base}/digital-employees/{id}/freeze`,
    tag: "incidents",
    summary:
      "Freeze one DE: its writes refused, its queued runs held, its queued sends held or cancelled; opens an incident",
    access: "manager",
    params: CompanyItemParams,
    body: FreezeBody,
    response: { status: 201, schema: IncidentView },
    errors: ["conflict"],
    emits: ["incident.opened"],
  }),
  "incidents.list": defineRoute({
    ...core,
    id: "incidents.list",
    method: "get",
    path: `${base}/incidents`,
    tag: "incidents",
    summary: "The company's incidents (freezes first of all), newest first",
    access: "manager",
    params: CompanyParams,
    query: IncidentListQuery,
    response: { status: 200, schema: IncidentList },
    errors: [],
    emits: [],
  }),
  "incidents.resolve": defineRoute({
    ...core,
    id: "incidents.resolve",
    method: "post",
    path: `${base}/incidents/{id}/resolve`,
    tag: "incidents",
    summary: "Resolve an incident; a freeze is lifted (its held runs and sends released)",
    access: "manager",
    params: CompanyItemParams,
    body: IncidentResolveBody,
    response: { status: 200, schema: IncidentView },
    errors: ["conflict"],
    emits: ["incident.resolved"],
  }),
};
